/**
 * The projections, held in memory while the whole log is replayed (`rebuildProjections` in
 * `db/aplicar.ts`).
 *
 * A whole replay used to apply the log an event at a time against the database, and nearly all
 * its time went to waiting: a profile showed 8.8 s of 15.9 s idle on IndexedDB round trips and
 * 0.19 s in the reducers (AUDITORIAS.md item 2). Here every read and write is a Map, and the
 * result goes into the database in one write per table at the end. The same reducers apply the
 * same effects through the same `aplicarEfectos`, so which write wins is still decided by one
 * rule, not two.
 *
 * The tables are built from the database's own schema — names, primary keys, which keys
 * increment — so a projection added there is here too. Rows go in and come out as copies, as
 * IndexedDB's do: a reducer that changed a row it had read would otherwise be changing the table
 * behind the replay's back.
 */
import type { Table } from 'dexie';
import { existenciaId, saldoId, type ConteoDeCaja, type EventoDelLog, type Reader } from '@abastece/core';
import type { Centavos, Settings } from '@abastece/shared';
import { tablasProyectadas } from './dexie.js';
import type { Tablas } from './effects.js';
import { primerConteo, settingsDe } from './reader.js';

type Clave = string | number;
type Fila = Record<string, unknown>;

/** A row the index leaves out, as IndexedDB leaves out a row whose indexed field is no key. */
const FUERA = Symbol('fuera');

/**
 * The lookups the reader makes, one index each. The database answers some by index and some by
 * reading the table through; either way the answer is every matching row in primary key order,
 * and so is this. The ones asked on every sale must not read the table through here: at a year
 * of trading that is the difference between seconds and minutes.
 */
const INDICES: Record<string, Record<string, (f: Fila) => unknown>> = {
  usuarios: { nombre: (f) => f['nombre'] },
  dispositivos: { codigo: (f) => f['codigo'] },
  existencias: { ubicacion: (f) => f['ubicacion'], productoId: (f) => f['productoId'] },
  unidades: { productoId: (f) => f['productoId'] },
  sesiones: { abiertaDe: (f) => (f['estado'] === 'abierta' ? f['dispositivoId'] : FUERA) },
  lotes: { de: (f) => lugar(f['productoId'], f['ubicacion']) },
  jornadas: { abiertaDe: (f) => (f['estado'] === 'abierta' ? f['camion'] : FUERA) },
  planillas: { dePeriodo: (f) => (f['esLiquidacion'] ? FUERA : f['periodo']) },
  saldosDiarios: { cuenta: (f) => f['cuenta'] },
  asientos: { eventId: (f) => f['eventId'] },
  gastos: { faltanteDe: (f) => f['faltanteDe'] ?? FUERA },
  devoluciones: { ventaId: (f) => f['ventaId'] },
  incobrables: { clienteId: (f) => f['clienteId'] },
  ofertas: { productoId: (f) => f['productoId'] },
};

const lugar = (productoId: unknown, ubicacion: unknown) => JSON.stringify([productoId, ubicacion]);

/** Rows handed to the database per write (`volcar`). */
const FILAS_POR_ESCRITURA = 500;

/** IndexedDB's order for the keys a projection uses: numbers first, then strings by code unit. */
function porClave(a: Clave, b: Clave): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'number') return -1;
  if (typeof b === 'number') return 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** One projected table: its rows by primary key, the indexes above, and what `aplicarEfectos` asks of a table. */
class TablaMemoria {
  readonly filas = new Map<Clave, Fila>();
  private readonly indices = new Map<string, { clave: (f: Fila) => unknown; filas: Map<unknown, Set<Clave>> }>();
  private siguiente = 1;
  /** Counts every write, so a reading worked out from the whole table knows when it is stale. */
  version = 0;

  constructor(
    readonly name: string,
    private readonly primaria: string,
    private readonly autoincremento: boolean,
  ) {
    for (const [nombre, clave] of Object.entries(INDICES[name] ?? {})) this.indices.set(nombre, { clave, filas: new Map() });
  }

  async get(k: Clave): Promise<Fila | undefined> {
    const fila = this.filas.get(k);
    return fila && structuredClone(fila);
  }

  /** As IndexedDB's: a row with no key, in a table whose keys increment, is given the next one. */
  async put(fila: Fila): Promise<Clave> {
    let copia = structuredClone(fila);
    let k = copia[this.primaria] as Clave | undefined;
    if (k === undefined) {
      if (!this.autoincremento) throw new Error(`${this.name}: fila sin clave`);
      k = this.siguiente;
      copia = { ...copia, [this.primaria]: k };
    }
    if (this.autoincremento && typeof k === 'number' && k >= this.siguiente) this.siguiente = Math.floor(k) + 1;
    this.sacar(k);
    this.filas.set(k, copia);
    for (const { clave, filas } of this.indices.values()) {
      const valor = clave(copia);
      if (valor === FUERA) continue;
      let claves = filas.get(valor);
      if (!claves) filas.set(valor, (claves = new Set()));
      claves.add(k);
    }
    this.version++;
    return k;
  }

  async delete(k: Clave): Promise<void> {
    this.sacar(k);
    this.filas.delete(k);
    this.version++;
  }

  private sacar(k: Clave): void {
    const antes = this.filas.get(k);
    if (!antes) return;
    for (const { clave, filas } of this.indices.values()) filas.get(clave(antes))?.delete(k);
  }

  /** Copies of the rows an index files under `valor`, in primary key order. */
  donde(indice: string, valor: unknown): Fila[] {
    const claves = this.indices.get(indice)?.filas.get(valor);
    if (!claves?.size) return [];
    return [...claves].sort(porClave).map((k) => structuredClone(this.filas.get(k)!));
  }

  /** Copies of the rows that pass, in primary key order, as Dexie's `filter` gives them. */
  filtrar(pasa: (f: Fila) => boolean): Fila[] {
    return [...this.filas.entries()]
      .filter(([, f]) => pasa(f))
      .sort(([a], [b]) => porClave(a, b))
      .map(([, f]) => structuredClone(f));
  }
}

/** Every projection in memory, the `Reader` over them, and the one write that puts them in the database. */
export class Memoria {
  private readonly t: Record<string, TablaMemoria>;
  private configuracion: { version: number; valor: Settings } | null = null;

  /**
   * `delLog`: every event in the log by its id, refused ones too, its type and when it was made (`eventoDelLog`).
   * `conteos`: every till's opening and corte in it, refused ones too (`conteoDeCaja`).
   * `dinero`: every count of money in it, in its place (`primerConteoEntre`), and `refusados`, what
   * the books have refused of the log so far, which the replay adds to as it goes.
   */
  constructor(
    private readonly delLog: ReadonlyMap<string, EventoDelLog> = new Map(),
    private readonly conteos: ReadonlyMap<string, ConteoDeCaja> = new Map(),
    private readonly dinero: readonly { id: string; type: string; payload: unknown; lugar: number }[] = [],
    private readonly refusados: ReadonlySet<string> = new Set(),
  ) {
    this.t = Object.fromEntries(
      tablasProyectadas().map((tabla: Table) => [
        tabla.name,
        new TablaMemoria(tabla.name, String(tabla.schema.primKey.keyPath), Boolean(tabla.schema.primKey.auto)),
      ]),
    );
  }

  /** What `aplicarEfectos` writes to. */
  get tablas(): Tablas {
    return this.t as unknown as Tablas;
  }

  /**
   * The same contract as `localReader`, answered from memory. Every answer is what the database
   * would give for the same rows: the first match is the one with the lowest primary key, and a
   * list comes in primary key order.
   */
  readonly reader: Reader = (() => {
    // Rows in memory carry no types; the contract's own signatures say what each one is.
    const t = (nombre: string) => this.t[nombre]!;
    const leer = (nombre: string) => async (id: string) => (await t(nombre).get(id)) as never;
    const primera = (filas: Fila[]) => filas[0] as never;
    const lista = (filas: Fila[]) => filas as never;
    return {
      usuario: leer('usuarios'),
      hayUsuarios: async () => t('usuarios').filas.size > 0,
      saldoCuenta: async (codigo, hasta) => {
        let saldo = 0;
        for (const d of t('saldosDiarios').donde('cuenta', codigo)) {
          if (hasta !== undefined && (d['fecha'] as string) > hasta) continue;
          saldo += (d['debe'] as number) - (d['haber'] as number);
        }
        return saldo as Centavos;
      },
      saldoCuentaDolares: async (codigo, hasta) => {
        let dolares = 0;
        for (const d of t('saldosDiarios').donde('cuenta', codigo)) {
          if (hasta !== undefined && (d['fecha'] as string) > hasta) continue;
          dolares += (d['dolares'] as number | undefined) ?? 0;
        }
        return dolares as Centavos;
      },
      usuarioPorNombre: async (nombre) => primera(t('usuarios').donde('nombre', nombre)),
      dispositivo: leer('dispositivos'),
      dispositivoPorCodigo: async (codigo) => primera(t('dispositivos').donde('codigo', codigo)),
      // Read on every event, and it changes only when a setting does: worked out once per change.
      config: async () => {
        const tabla = t('config');
        if (this.configuracion?.version !== tabla.version) {
          this.configuracion = { version: tabla.version, valor: settingsDe([...tabla.filas.values()] as never) };
        }
        return structuredClone(this.configuracion.valor);
      },

      producto: leer('productos'),
      unidadVenta: leer('unidades'),
      unidadesDeProducto: async (productoId) => lista(t('unidades').donde('productoId', productoId)),
      listaPrecio: leer('listas'),
      proveedor: leer('proveedores'),
      existencia: async (productoId, ubicacion) => (await t('existencias').get(existenciaId(productoId, ubicacion))) as never,
      compra: leer('compras'),
      sesionCaja: leer('sesiones'),
      sesionAbiertaDe: async (dispositivoId) => primera(t('sesiones').donde('abiertaDe', dispositivoId)),
      venta: leer('ventas'),
      comprasDeProducto: async (productoId, ubicacion) =>
        lista(
          t('compras').filtrar(
            (c) => c['ubicacion'] === ubicacion && (c['lineas'] as { productoId: string }[]).some((l) => l.productoId === productoId),
          ),
        ),
      ventasDeProducto: async (productoId, ubicacion, desde) =>
        lista(
          t('ventas').filtrar(
            (v) =>
              (v['fecha'] as string) >= desde &&
              v['ubicacion'] === ubicacion &&
              (v['lineas'] as { productoId: string | null }[]).some((l) => l.productoId === productoId),
          ),
        ),
      saldoTelefonico: async (operadora) => (await t('saldos').get(saldoId(operadora))) as never,
      recarga: leer('recargas'),
      lotesDe: async (productoId, ubicacion) => lista(t('lotes').donde('de', lugar(productoId, ubicacion))),
      existenciasDe: async (ubicacion) => lista(t('existencias').donde('ubicacion', ubicacion)),
      existenciasDeProducto: async (productoId) => lista(t('existencias').donde('productoId', productoId)),

      cliente: leer('clientes'),
      pedido: leer('pedidos'),
      pedidosPendientes: async () => lista(t('pedidos').filtrar((p) => p['estado'] === 'pendiente')),
      jornada: leer('jornadas'),
      jornadaAbiertaDe: async (camion) => primera(t('jornadas').donde('abiertaDe', camion)),
      gasto: leer('gastos'),
      movimientoDueno: leer('movimientosDueno'),

      empleado: leer('empleados'),
      empleadosActivos: async () => lista(t('empleados').filtrar((e) => Boolean(e['activo']))),
      planilla: leer('planillas'),
      // In id order, as the database gives them: by period, a run refused for overlapping two named the other one.
      planillasDePeriodos: async (periodos) =>
        lista(periodos.flatMap((periodo) => t('planillas').donde('dePeriodo', periodo)).sort((a, b) => porClave(a.id as Clave, b.id as Clave))),
      pagoCotizaciones: leer('pagosCotizaciones'),
      pagoAguinaldo: leer('pagosAguinaldo'),
      oferta: leer('ofertas'),
      ofertasDeProducto: async (productoId) => lista(t('ofertas').donde('productoId', productoId)),

      abono: leer('abonos'),
      incobrable: leer('incobrables'),
      incobrablesDe: async (clienteId) => lista(t('incobrables').donde('clienteId', clienteId)),
      deposito: leer('depositos'),
      transferenciaBanco: leer('transferenciasBanco'),
      devolucion: leer('devoluciones'),
      devolucionesDeVenta: async (ventaId) => lista(t('devoluciones').donde('ventaId', ventaId)),
      devolucionCompra: leer('devolucionesCompra'),
      asientosDeEvento: async (eventoId) => lista(t('asientos').donde('eventId', eventoId)),
      gastosDeFaltante: async (eventoId) => lista(t('gastos').donde('faltanteDe', eventoId)),
      eventoDelLog: async (eventoId) => this.delLog.get(eventoId) ?? null,
      conteoDeCaja: async (eventoId) => this.conteos.get(eventoId) ?? null,
      relevoDe: async (sesionId) => primera(t('sesiones').filtrar((s) => s['relevoDe'] === sesionId)),
      primerConteoEntre: async (de, desde, hasta) => {
        const rutas = 'general' in de ? [] : t('jornadas').filtrar((j) => j['sesionId'] === de.sesionId);
        return primerConteo(this.dinero, (id) => this.refusados.has(id), new Set(rutas.map((j) => String(j['id']))), de, desde, hasta);
      },
    } satisfies Reader;
  })();

  /**
   * Everything into the database, inside the caller's transaction, a few hundred rows at a time.
   * Handing IndexedDB every row at once copies them all in one go on the page's own thread: 69,000
   * rows at 90 days of trading froze the screen for 1.6 s on a desktop and 8 s at a cheap phone's
   * speed. Between batches the screen draws, and the batch in flight keeps the transaction open.
   */
  async volcar(): Promise<void> {
    for (const tabla of tablasProyectadas() as Table[]) {
      const filas = [...this.t[tabla.name]!.filas.values()];
      for (let i = 0; i < filas.length; i += FILAS_POR_ESCRITURA) {
        await tabla.bulkPut(filas.slice(i, i + FILAS_POR_ESCRITURA));
      }
    }
  }
}
